// Full-document render of the Oscar thank-you (order confirmation) view:
// PageSkeleton (the React document shell) wrapping the legacy stearns layout --
// the "Rest easy" intro with the create-account island, the order-number/help
// content title, the site-local OrderDetail block (the .thank-you-summary
// sections + .thank-you-details Order Summary panel, typed-serialized
// server-side by server/cms/order_detail.py), and the e-signature / support
// footer. The deferred GA4 dataLayer events (ga_ecom + ga_purchase) and Google
// Seller Ratings survey opt-in (ga_seller) inject after hydration.
// Header/footer slots are omitted so the default site chrome renders.
import React from "react";
import { DataLayerPush } from "tsi-common-react/src/components/DataLayerPush/index";
import { GASeller } from "tsi-common-react/src/components/GASeller/index";

import { templates } from "@reactivated";

import { OrderDetail } from "../components/OrderDetail";
import { PageSkeleton } from "../components/PageSkeleton";
import { Template as CreateAccount } from "./CreateAccountTemplate";

export const Template = (
    props: templates.ThankYouDocumentTemplate,
): React.JSX.Element => {
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <div className="thank-you-container l-capped-width u-margin-bottom--xl u-full-width-title">
                <div className="thank-you">
                    <div className="intro">
                        <h3 className="intro__title">
                            Rest easy, <span>{props.first_name}.</span>{" "}
                            We&apos;re working on your order.
                        </h3>
                        <p className="intro__copy">
                            You&apos;ll receive order confirmation emails at
                            each step of your order&apos;s progress, at{" "}
                            <strong>{props.email}</strong>. Check your order
                            status anytime at{" "}
                            <a
                                className="intro__link"
                                href={props.order_list_url}
                            >
                                stearnsandfoster.com
                            </a>
                            . Thanks for shopping with us.
                        </p>

                        {props.create_account && (
                            <CreateAccount {...props.create_account} />
                        )}
                    </div>

                    <div className="content-title">
                        <h4 className="content-title__headline">
                            <span className="content-title__label">
                                Order Number:
                            </span>{" "}
                            {props.order_number} | {props.order_date}
                        </h4>
                        <span className="content-title__info">
                            Need Help?{" "}
                            <a href={props.cs_phone_rfc3966}>
                                {props.cs_phone_national}
                            </a>
                        </span>
                    </div>
                </div>

                {/* order_detail is the stearns shape (the shared template types
                    it nullable; on stearns the thank_you_order_detail hook
                    always populates it). The e-signature companions ride on it
                    because they are serialized by the same site builder. */}
                {props.order_detail && (
                    <OrderDetail data={props.order_detail} />
                )}

                <div className="thank-you-esig">
                    {props.order_detail?.has_esignature && (
                        <div className="thank-you-esig__copy">
                            <span className="thank-you-esig__copy--bold">
                                Electronic Signature:
                            </span>{" "}
                            Acknowledged &amp; Signed on{" "}
                            {props.order_detail.esig_signed_date}{" "}
                            {props.order_detail.esig_signed_time}
                        </div>
                    )}
                    <div className="thank-you-esig__copy">
                        Questions or concerns? Talk to a Sleep Expert at{" "}
                        <a
                            className="thank-you-esig__copy--bold"
                            href={props.cs_phone_rfc3966}
                        >
                            {props.cs_phone_national}
                        </a>
                        .
                    </div>
                </div>
            </div>
            <DataLayerPush events={props.ga_events} />
            <GASeller surveyData={props.ga_seller_survey_data} />
        </PageSkeleton>
    );
};
